import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Power,
  Trash2,
  MapPin,
  DollarSign,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import type { Vacancy } from "@shared/types";

export default function VacancyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id) loadVacancy(parseInt(id));
  }, [id]);

  const loadVacancy = async (vacancyId: number) => {
    try {
      const response = await apiClient.admin.vacancies.get(vacancyId);
      setVacancy(response.data as Vacancy);
    } catch (error) {
      console.error("Failed to load vacancy:", error);
      toast.error("Failed to load vacancy");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!vacancy || !id) return;
    try {
      await apiClient.admin.vacancies.update(parseInt(id), {
        isActive: !vacancy.isActive,
      });
      setVacancy({ ...vacancy, isActive: !vacancy.isActive });
      toast.success(vacancy.isActive ? "Vacancy deactivated" : "Vacancy activated");
    } catch (error) {
      console.error("Failed to toggle vacancy:", error);
      toast.error("Failed to update vacancy");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await apiClient.admin.vacancies.delete(parseInt(id));
      toast.success("Vacancy deleted");
      navigate("/admin/vacancies");
    } catch (error) {
      console.error("Failed to delete vacancy:", error);
      toast.error("Failed to delete vacancy");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Vacancy not found</p>
        <Button onClick={() => navigate("/admin/vacancies")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Vacancies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin/vacancies")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{vacancy.title}</h1>
            <Badge
              className={
                vacancy.isActive
                  ? "bg-green-500 text-white"
                  : "bg-gray-500 text-white"
              }
            >
              {vacancy.isActive ? "Active" : "Inactive"}
            </Badge>
          </div>
          {vacancy.titleEs && (
            <p className="text-lg text-muted-foreground">{vacancy.titleEs}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/admin/vacancies/${id}/edit`}>
            <Button variant="outline">
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </Link>
          <Button variant="outline" onClick={handleToggleActive}>
            <Power
              className={`mr-2 h-4 w-4 ${
                vacancy.isActive ? "text-green-500" : "text-muted-foreground"
              }`}
            />
            {vacancy.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button variant="outline" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="mr-2 h-4 w-4 text-destructive" />
            Delete
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        {vacancy.location && (
          <span className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {vacancy.location}
          </span>
        )}
        {vacancy.payRate && (
          <span className="flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            {vacancy.payRate}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Calendar className="h-4 w-4" />
          Created {formatDate(vacancy.createdAt)}
        </span>
        {vacancy.updatedAt !== vacancy.createdAt && (
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Updated {formatDate(vacancy.updatedAt)}
          </span>
        )}
      </div>

      {/* Description */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Description (English)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">
              {vacancy.description || "No description provided"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Description (Spanish)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-muted-foreground">
              {vacancy.descriptionEs || "No Spanish description provided"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Requirements */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Requirements (English)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap">
              {vacancy.requirements || "No requirements specified"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Requirements (Spanish)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-muted-foreground">
              {vacancy.requirementsEs || "No Spanish requirements specified"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vacancy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{vacancy.title}"? This will
              deactivate the vacancy.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
